import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DialogClose } from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, AlertCircle } from "lucide-react";

interface FeedbackDialogProps {
  onClose: () => void;
  initialFeedback?: string;
}

export const FeedbackDialog = ({ onClose, initialFeedback = "" }: FeedbackDialogProps) => {
  const [feedback, setFeedback] = useState(initialFeedback);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('submit-user-feedback', {
        body: { feedback }
      });

      if (error) throw error;

      toast({
        title: "Feedback Submitted",
        description: (
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            Thank you for helping us improve LinkPilot!
          </div>
        ),
        duration: 3000,
      });
      
      setFeedback("");
      onClose();
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast({
        title: "Error",
        description: (
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-destructive" />
            Failed to submit feedback. Please try again.
          </div>
        ),
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <Textarea
        placeholder="Ideas on how to improve LinkPilot. Use the in-app Intercom Support chat for technical issues."
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        className="min-h-[100px] bg-background"
        disabled={isSubmitting}
      />
      <div className="flex items-center gap-2">
        <DialogClose asChild>
          <Button variant="outline" disabled={isSubmitting}>
            Cancel
          </Button>
        </DialogClose>
        <Button
          variant="outline"
          onClick={() => setFeedback("")}
          disabled={isSubmitting}
        >
          Clear
        </Button>
        <Button
          className="bg-primary/10 hover:bg-primary/20 text-primary ml-auto"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Sending..." : "Send feedback"}
        </Button>
      </div>
      <div className="text-sm text-muted-foreground">
        Have a technical issue? Please reach out via the in-app Support Chat
        {" "}or{" "}
        <a 
          href="https://intercom.help/linkpilot-app/en/collections/11293272-general" 
          className="text-primary hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          browse our help center
        </a>
        .
      </div>
    </div>
  );
};